import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { type TreeMenuItem, useLink } from '@refinedev/core';
import { CaretDownIcon, ListIcon, XIcon } from '@phosphor-icons/react';
import { IERPUser } from '@admin-panel/types';
import { groupTopLevel, getIcon, isTreeItemActive, getFirstNavigableRoute } from './helpers';
import AvatarPopover from './AvatarPopover';
import { Button } from 'antd';
import { getAppName, PORTAL_NAME } from '@admin-panel/config/env';
import { WarehouseFacilitySelector, VendorSelector } from '../header';
import { useHeaderTools } from '../Layout/header-tools-context';
import * as Styles from './styles';

type LinkComponent = ReturnType<typeof useLink>;

type Props = {
  menuItems: Array<TreeMenuItem>;
  selectedKey?: string | null;
  Link: LinkComponent;
  user?: IERPUser;
  onLogout: () => void;
};

const MobileSider: React.FC<Props> = ({ menuItems, selectedKey, Link, user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const headerTools = useHeaderTools();
  const topLevel = useMemo(() => groupTopLevel(menuItems), [menuItems]);
  const defaultOpenKey = useMemo(() => {
    for (const group of topLevel) {
      if (group.children?.some(c => c.key === selectedKey)) return group.key;
    }
    return null as string | null;
  }, [topLevel, selectedKey]);
  const [openKey, setOpenKey] = useState<string | null>(defaultOpenKey);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      <Styles.MobileHeader>
        <Button
          aria-label="Open navigation"
          type="text"
          icon={<ListIcon size={20} />}
          onClick={() => setOpen(true)}
        />

        {PORTAL_NAME === 'warehouse' && <WarehouseFacilitySelector user={user} />}
        {PORTAL_NAME === 'vendor' && <VendorSelector compact user={user} />}

        {headerTools ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            {headerTools}
            <AvatarPopover
              user={user}
              onLogout={onLogout}
              placement="bottomRight"
              trigger={['hover', 'click']}
            />
          </div>
        ) : (
          <AvatarPopover
            user={user}
            onLogout={onLogout}
            placement="bottomRight"
            trigger={['hover', 'click']}
          />
        )}
      </Styles.MobileHeader>
      <AnimatePresence>
        {open && (
          <>
            <Styles.MobileOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <Styles.MobileDrawer
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.2 }}
            >
              <div
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  height: 56,
                  padding: '0 12px',
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                  background: '#fff',
                  flexShrink: 0,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 16 }}>{getAppName()}</div>
                <Button
                  aria-label="Close navigation"
                  type="text"
                  icon={<XIcon size={20} />}
                  onClick={() => setOpen(false)}
                />
              </div>
              <div style={{ padding: 12 }}>
                {topLevel.map(group => {
                  const IconNode = getIcon(group);
                  const isActive = isTreeItemActive(group, selectedKey);
                  const hasChildren = !!(group.children && group.children.length);
                  const topActive = isActive || openKey === group.key;
                  const route = getFirstNavigableRoute(group) || '';
                  return (
                    <div key={group.key} style={{ marginBottom: 10 }}>
                      {hasChildren ? (
                        <button
                          type="button"
                          onClick={() =>
                            setOpenKey(prev => (prev === group.key ? null : group.key))
                          }
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            fontWeight: topActive ? 700 : 600,
                            width: '100%',
                            background: topActive ? 'rgba(57,58,251,0.06)' : 'transparent',
                            border: 'none',
                            padding: '14px 8px',
                            textAlign: 'left',
                            color: topActive ? '#393AFB' : 'inherit',
                            cursor: 'pointer',
                            borderRadius: 10,
                          }}
                        >
                          <span style={{ display: 'inline-flex', fontSize: 18 }}>{IconNode}</span>
                          <span style={{ fontSize: 16, flex: 1 }}>{group.label ?? group.name}</span>
                          <span
                            style={{
                              display: 'inline-flex',
                              transition: 'transform 0.2s',
                              transform: openKey === group.key ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                          >
                            <CaretDownIcon />
                          </span>
                        </button>
                      ) : (
                        <Link
                          to={route}
                          style={{ color: 'inherit' }}
                          onClick={() => setOpen(false)}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              fontWeight: isActive ? 700 : 600,
                              width: '100%',
                              background: isActive ? 'rgba(57,58,251,0.06)' : 'transparent',
                              padding: '14px 8px',
                              color: isActive ? '#393AFB' : 'inherit',
                              borderRadius: 10,
                            }}
                          >
                            <span style={{ display: 'inline-flex', fontSize: 18 }}>{IconNode}</span>
                            <span style={{ fontSize: 16, flex: 1 }}>
                              {group.label ?? group.name}
                            </span>
                          </div>
                        </Link>
                      )}
                      <AnimatePresence initial={false}>
                        {hasChildren && openKey === group.key && (
                          <motion.div
                            key="collapse"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            style={{ overflow: 'hidden' }}
                          >
                            <div
                              style={{
                                marginTop: 6,
                                paddingLeft: 34,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 10,
                              }}
                            >
                              {group.children!.map(child => {
                                const isChildActive = selectedKey === child.key;
                                const ChildIcon = getIcon(child);
                                return (
                                  <Link
                                    to={child.list ?? child.route ?? ''}
                                    key={child.key}
                                    style={{ color: 'inherit' }}
                                    onClick={() => setOpen(false)}
                                  >
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        padding: '12px 6px',
                                        borderRadius: 8,
                                        fontSize: 15,
                                        fontWeight: isChildActive ? 700 : 500,
                                        color: isChildActive ? '#393AFB' : 'inherit',
                                        background: isChildActive
                                          ? 'rgba(57,58,251,0.06)'
                                          : 'transparent',
                                      }}
                                    >
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          fontSize: 16,
                                        }}
                                      >
                                        {ChildIcon}
                                      </span>
                                      <span style={{ flex: 1 }}>{child.label ?? child.name}</span>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </Styles.MobileDrawer>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileSider;
